import {
  businessEmployeeAccessControlFactory,
  BusinessEmployeePermissions,
} from '@/utils/access-control';
import { useGeneratedSalesPortalEmployeePermissionsQuery } from '@/domain/access-control/blocs/documents/employee-permissions.generated';
import { useMemo } from 'react';

/**
 * Loader for employee permissions via `businesses_employees.employee_permissions`.
 *
 * Returns an access control factory (`can`) for checking any permission subject/action
 * pair.
 *
 * @param supplierUUID - The UUID of the supplier
 * @param userUUID - The authenticated user's UUID
 * @returns `can` — access control check function, and `loading` state
 *
 * @example
 * ```typescript
 * const { can, loading } = useEmployeePermissionsLoader(supplierUUID, userUUID);
 *
 * const isPriceEditingEnabled =
 *   can(PermissionAction.Write, PermissionSubject.SrtPriceEditing) &&
 *   salesRepConfig?.override_mode !== SalesRepOverrideMode.DISABLED;
 * ```
 */
export const useEmployeePermissionsLoader = (
  supplierUUID: string,
  userUUID: string | null,
) => {
  const { data, loading } = useGeneratedSalesPortalEmployeePermissionsQuery({
    skip: !userUUID || !supplierUUID,
    variables: {
      supplierUUID,
      userUUID: userUUID!,
    },
  });

  const can = useMemo(() => {
    const rawPermissions =
      data?.businesses_by_pk?.businesses_employees?.[0]?.employee_permissions;
    const permissions: Partial<BusinessEmployeePermissions> | undefined =
      rawPermissions && typeof rawPermissions === 'object'
        ? rawPermissions
        : undefined;
    return businessEmployeeAccessControlFactory(permissions);
  }, [data]);

  return { can, loading };
};
